
// Vue - Vuex
import { Component, Vue, Emit, Watch, Prop, Ref } from "vue-property-decorator";
import { Getter, namespace, Mutation } from "vuex-class";


// Mixins
import { formatDateMixin } from "@/mixins";

// Services
import getScript from "@/services/insertScript.service";

// Components
import EventOutcomeSelector from "@/components/EventOutcomeSelector.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import IncidentReport from "@/components/IncidentReport.vue";
import MapSearch from "@/components/mapping/MapSearch.vue";
import { Datetime } from "vue-datetime";
import vSelect from "vue-select";

// Utilities
import { getMapStyle } from "@/scripts/mapping/mapStyle";
import GoogleMap from "@/scripts/mapping/map";
import { Settings } from "luxon";
import { get, cloneDeep } from "lodash";

// Types
import { EventSearchState, EventSearchResult, EventType, Category } from '@/store/event-search/types';
import { FeaturesList } from "@/store/types";

// Filter components.
import FindWithTagFilter from './event-search/FindWithTagFilter.vue';
import DeviceTypeFilter from './event-search/DeviceTypeFilter.vue';
import AlarmTagsFilter from './event-search/AlarmTagsFilter.vue';
import AlarmTypeFilter from './event-search/AlarmTypeFilter.vue';
import OperatorFilter from './event-search/OperatorFilter.vue';
import AreaFilter from './event-search/AreaFilter.vue';
import { MapLayersState } from '@/store/map-layers/types';
import EventSearchModel from '@/types/eventSearch/EventSearchModel';
import EventTypeFilter from './event-search/EventTypeFilter.vue';

// Vuex modules
const EventSearchStore = namespace("eventSearch");
const MapLayers = namespace("mapLayers");
const Setup = namespace("setup");

const {
	weekAgoDateFormatted,
	endOFDayFormatted
} = formatDateMixin.methods;
Settings.defaultLocale = "en";

@Component({
	components: {
		"v-select": vSelect,
		"map-search": MapSearch,
		datetime: Datetime,
		"event-outcome-selector": EventOutcomeSelector,
		"incident-report": IncidentReport,
		"area-tree-select": AreaTreeSelect,
		"alarm-tags-filter": AlarmTagsFilter,
		"alarm-type-filter": AlarmTypeFilter,
		"device-type-filter": DeviceTypeFilter,
		"operator-filter": OperatorFilter,
		"find-with-tag-filter": FindWithTagFilter,
		"area-filter": AreaFilter,
		"event-type-filter": EventTypeFilter,
	},
})
export default class EventSearch extends Vue {
	@Ref("mappingContainer") mapContainerRef: HTMLElement;

	@MapLayers.State((s: MapLayersState) => s.MapLayersBounds) mapLayersBounds!: google.maps.LatLngBoundsLiteral;
	@EventSearchStore.State((s: EventSearchState) => s.SearchableEventTypes) searchableEventTypes!: EventType[];
	@EventSearchStore.State((s: EventSearchState) => s.Categories) eventCategories!: Category[];
	@EventSearchStore.State((s: EventSearchState) => s.Events) events!: EventSearchResult[];

	@EventSearchStore.Action loadEventCategories!: () => Promise<void>;
	@EventSearchStore.Action loadDeviceTypes!: () => Promise<void>;
	@MapLayers.Action loadMapLayersBounds!: () => Promise<void>;


	@Getter("getMapKey") private mapKey: string;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter getHideLabels?: boolean;
	@Getter getMapType?: string;

	@Mutation setMapType!: (mapType: string) => void;

	private mapType: string = "";
	private isMapVisible: boolean = false;
	private map: GoogleMap | null = null;
	private selectedMapArea: google.maps.Polygon | null = null;
	private mapInitialized: boolean = false;
	private clearClicked: boolean = false;
	private dataLoaded: boolean = false;

	@Prop({ default: 0, type: Number }) private eventId: number;

	private get defaultSearchFilters() {
		return {
			from: weekAgoDateFormatted(),
			until: endOFDayFormatted(),
			groupID: 0,
			tags: [],
		};
	}

	private allEventTypeFilterOption: EventType = {
		eventTypeID: -1,
		title: "All",
		searchable: true,
	};

	private searchModelTemplate: EventSearchModel = {
		from: weekAgoDateFormatted(),
		until: endOFDayFormatted(),
		groupID: 0,
		boundaryPath: null,
		eventTypeId: null,
		deviceType: null,
		alarmType: null,
		category: null,
		operator: null,
		flaggedEventsOnly: false,
		tags: [],
		allTags: false,
		alarmTags: [],
		IncludeAutoHandled: true,
		allAlarmTags: false,
	};

	private searchModel: EventSearchModel | null = cloneDeep(this.searchModelTemplate);

	/** -- Lifecycle Hooks -- */

	/**
	 * @summary created lifecycle hook
	 * - fetches event categories
	 * - fetches map layer bounds
	 */
	public async created(): Promise<void> {
		this.searchModel = cloneDeep(this.searchModelTemplate);
		await this.loadDeviceTypes();
		await this.loadEventCategories();
		await this.loadMapLayersBounds();

		this.dataLoaded = true;
	}

	/** -- Methods -- */

	public outcomeChanged(value) {
		this.$set(this.searchModel, "category", value === 0 ? null : value);
	}

	/** -- Computed properties -- */

	/**
	 * @summary computed property which retrieves the event categories for
	 * event category filter.
	 */
	public get eventCategoriesMap() {
		if (!this.eventCategories) {
			return [];
		}

		return this.eventCategories.map((category) => {
			return {
				id: category.eventOutcomeID,
				parentId: category.parentID,
				title: category.title,
			};
		});
	}

	/**
	 * @summary Computed property which will get the polygon
	 * options for drawing on the map
	 */
	public get polygonOptions() {
		return {
			strokeColor: this.$config.colors.POLYGON_STROKE,
			strokeOpacity: 1,
			strokeWeight: 4,
			fillColor: this.$config.colors.POLYGON_FILL,
			fillOpacity: 0.35,
		};
	}

	/**
	 * @summary Computed property to get the feature flag for
	 * Alarms/SiteMonitor/EventFlagging
	 */
	public get isEventFlaggingEnabled() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "EventFlagging"], false);
	}

	/**
	 * @summary Computed property to get the feature flag for
	 * Alarms/EventSearch/EventTypeFilter
	 */
	private get isEventTypeFilteringEnabled() {
		return get(this.featuresList, ["Alarms", "EventSearch", "EventTypeFilter"], false);
	}

	/**
	 * @summary Computed property to get the feature flag for
	 * Alarms/EventSearch/ShowAutohandledFilter
	 */
	private get isReturnAutoHandledEnabled() {
		return get(this.featuresList, ["Alarms", "EventSearch", "ShowAutohandledFilter"], false);
	}

	/**
	 * @summary Computed property to get the feature flag for
	 * Alarms/EventSearch/EventTagSearch
	 */
	private get isEventTagSearchEnabled() {
		return get(this.featuresList, ["Alarms", "EventSearch", "EventTagSearch"], false);
	}

	/**
	 * @summary Computed property to get the feature flag for
	 * Alarms/EventSearch/AlarmTag
	 */
	private get isFeatureAlarmTagSearchEnabled() {
		return get(this.featuresList, ["Alarms", "EventSearch", "AlarmTag"], false);
	}

	/**
	 * @summary Computed property to get all event type options and searchable event types.
	 */
	private get allSearchableEventTypes() {
		return [this.allEventTypeFilterOption, ...this.searchableEventTypes];
	}

	/**
	 * @summary Method to search the desired events by the filters applied.
	 * - also emits setSearchFilter
	 */
	@Emit("setSearchFilter")
	public search() {
		// @techdebt
		// Temporary solution. Waiting for API changes to remove this.
		const value = cloneDeep(this.searchModel);
		Object.keys(value).forEach((key) => {
			if (value[key] === null || value[key] === "") {
				delete value[key];
			}
		});

		const search = {
			...this.defaultSearchFilters,
			...value,
		};

		if (search.boundaryPath) {
			delete search.groupID;
		}

		if (search.eventTypeId === this.allEventTypeFilterOption.eventTypeID) {
			delete search.eventTypeId;
		}
		return search;
	}

	/**
	 * @summary Watcher which unsets the selected map area.
	 */
	@Watch("isMapVisible")
	public onMapVisibilityChange(value: boolean) {
		// Clear polygons on the map when hiding it
		if (!value && this.selectedMapArea) {
			this.selectedMapArea.setMap(null);
			this.selectedMapArea = null;
		}
	}

	/**
	 * @summary Watcher which will set the map zone filter on change.
	 */
	@Watch("selectedMapArea")
	public onMapFilterChange(zone) {
		if (zone) {
			const paths = this.getZonePaths(zone);
			this.searchModel.boundaryPath = paths.length ? JSON.stringify(paths) : null;
		} else {
			this.searchModel.boundaryPath = null;
		}
	}

	/**
	 * @summary Method to setup the map.
	 */
	public initMap() {
		if (this.mapInitialized) return;

		this.mapInitialized = true;
		getScript(
			`https://maps.googleapis.com/maps/api/js?key=` +
				`${this.mapKey}` +
				`&libraries=drawing,places,geometry`
		).then(() => {
			const mapPosition = { lat: 0, lng: 0, zoom: 25 };

			this.map = new GoogleMap(
				this.mapContainerRef,
				mapPosition,
				mapPosition.zoom,
				false,
				this.getMapType,
				getMapStyle(this.getMapType, this.getHideLabels)
			);
			this.mapType = this.getMapType;

			this.map!.registerEvent("onMapTypeChanged", (mapType: string) => {
				this.mapType = mapType;
				this.setMapType(mapType);

				this.map!.map.setOptions({
					styles: getMapStyle(this.getMapType, this.getHideLabels),
				});
			});

			const bounds = new google.maps.LatLngBounds(
				new google.maps.LatLng(this.mapLayersBounds.south, this.mapLayersBounds.west),
				new google.maps.LatLng(this.mapLayersBounds.north, this.mapLayersBounds.east)
			);
			this.map!.map.fitBounds(bounds);

			const drawingManager = new google.maps.drawing.DrawingManager({
				drawingMode: google.maps.drawing.OverlayType.POLYGON,
				drawingControl: true,
				drawingControlOptions: {
					position: google.maps.ControlPosition.TOP_CENTER,
					drawingModes: ["polygon"],
				},
				polygonOptions: this.polygonOptions,
			} as any);
			drawingManager.setMap(this.map!.map);

			// on zone add
			google.maps.event.addListener(drawingManager, "polygoncomplete", (zone: google.maps.Polygon) => {
				if (this.selectedMapArea != null) {
					this.selectedMapArea.setMap(null);
					this.selectedMapArea = null;
				}

				// @todo search by map position
				this.selectedMapArea = zone;
			});
		});
	}

	/**
	 * @summary Method to clear the search model.
	 */
	public clear() {
		this.searchModel = cloneDeep(this.searchModelTemplate);
		this.clearClicked = true;
	}

	/**
	 * @summary reset back to false to trigger clearClicked (watch)
	 * in EventOutcomeSelector on the next clear()
	 */
	public outcomeCleared() {
		this.clearClicked = false;
	}

	/**
	 * @summary Method to get zone path by zone.
	 */
	private getZonePaths(zone: google.maps.Polygon) {
		return zone
			.getPath()
			.getArray()
			.map((point) => {
				return { lat: point.lat(), lng: point.lng() };
			});
	}

	/**
	 * @summary Method to set bounds
	 */
	public setBounds(bounds: any) {
		if (bounds) {
			this.map!.map.fitBounds(
				new google.maps.LatLngBounds({ lat: bounds.south, lng: bounds.west }, { lat: bounds.north, lng: bounds.east })
			);
		}
	}

	/**
	 * @summary Method to set map location
	 */
	public setMapLocation(gpsData: any) {
		if (gpsData) {
			if (gpsData.viewport) {
				this.map!.map.fitBounds(gpsData.viewport);
			} else if (gpsData.location) {
				this.map!.map.setCenter(gpsData.location);
				this.map!.map.setZoom(17);
			}
		}
	}

	/**
	 * @summary method to set the map visibility.
	 */
	public toggleMapVisible() {
		if (this.isMapVisible) {
			this.isMapVisible = false;
		} else {
			this.isMapVisible = true;
			setTimeout(() => {
				this.initMap();
			}, 500);
		}
	}
}
